import React, { useState, useEffect } from 'react';
import './Timestamp.css';
import { addToHistory, getHistory } from '../../utils/cookieManager';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

const Timestamp = ({ onGenerateSuccess }) => {
  const [url, setUrl] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generatedVideoId, setGeneratedVideoId] = useState(null);
  const [error, setError] = useState(null);
  const [videoDetails, setVideoDetails] = useState(null);
  const [historyMatch, setHistoryMatch] = useState(null);


  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' },
  ];

  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = extractVideoId(url);

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showModal]);

  useEffect(() => {
    const fetchVideoDetails = async () => {
      if (!videoId) {
        setVideoDetails(null);
        setHistoryMatch(null);
        return;
      }

      // Check history for existing timestamps
      const history = getHistory();
      const match = history.find(item => item.videoId === videoId);
      if (match) {
        setHistoryMatch(match);
      } else {
        setHistoryMatch(null);
      }

      try {
        const apiKey = process.env.REACT_APP_YOUTUBE_APIKEY;
        if (!apiKey) {
          console.warn('YouTube API Key is missing');
          // Fallback to just thumbnail if API key is missing
          setVideoDetails({
            title: '',
            thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
          });
          return;
        }

        const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${apiKey}`);

        if (!response.ok) {
          throw new Error('Failed to fetch video details');
        }

        const data = await response.json();

        if (data.items && data.items.length > 0) {
          const snippet = data.items[0].snippet;
          const thumbnailUrl = snippet.thumbnails.maxres?.url || snippet.thumbnails.high?.url || snippet.thumbnails.medium?.url;

          setVideoDetails({
            title: snippet.title,
            thumbnail: thumbnailUrl
          });
        } else {
          // Fallback if video not found in API (e.g. private)
          setVideoDetails({
            title: '',
            thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
          });
        }
      } catch (err) {
        console.error('Error fetching video details:', err);
        // Fallback on error
        setVideoDetails({
          title: '',
          thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        });
      }
    };

    fetchVideoDetails();
    fetchVideoDetails();
  }, [videoId]);

  const handleViewSaved = () => {
    if (historyMatch) {
      setResult(historyMatch.result);
      setGeneratedVideoId(historyMatch.videoId);
      setShowModal(true);
    }
  };



  const handleGenerate = async () => {
    if (!videoId) {
      setError('Please enter a valid YouTube URL');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setGeneratedVideoId(null);
    setShowModal(false);

    try {
      const generateTimestamps = httpsCallable(functions, 'generate_timestamps');
      const response = await generateTimestamps({ url: url });

      const data = response.data;
      console.log('API Success:', data);

      // For now just console log as requested, but keeping the rest of the logic to show modal if data is valid
      setResult(data);
      setGeneratedVideoId(videoId);
      setShowModal(true);

      // Fetch title and save to history
      try {
        let videoTitle = videoDetails?.title || 'Unknown Video';

        // If title is missing (e.g. API failed), try noembed as backup
        if (!videoTitle || videoTitle === 'Unknown Video') {
          const oembedRes = await fetch(`https://noembed.com/embed?url=${url}`);
          const oembedData = await oembedRes.json();
          if (oembedData.title) videoTitle = oembedData.title;
        }

        const historyItem = {
          videoId,
          title: videoTitle,
          date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
          result: data
        };
        addToHistory(historyItem);
        // Update local match immediately so UI reflects it without reload
        setHistoryMatch(historyItem);

        if (onGenerateSuccess) onGenerateSuccess();
      } catch (e) {
        console.warn('Failed to save to history', e);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;

    let textToCopy = '';
    if (result.timestamps_string) {
      textToCopy = result.timestamps_string;
    } else if (Array.isArray(result.timestamps_list)) {
      textToCopy = result.timestamps_list.join('\n');
    } else if (typeof result.output === 'string') {
      textToCopy = result.output;
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      // Optional: Show a toast or change button text temporarily
      alert('Timestamps copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const timeToSeconds = (timeStr) => {
    const parts = timeStr.split(':').reverse();
    let seconds = 0;
    for (let i = 0; i < parts.length; i++) {
      seconds += parseInt(parts[i], 10) * Math.pow(60, i);
    }
    return seconds;
  };

  const renderTimestamps = () => {
    if (!result) return null;

    let timestamps = [];
    if (Array.isArray(result.timestamps_list)) {
      timestamps = result.timestamps_list;
    } else if (typeof result.timestamps_string === 'string') {
      timestamps = result.timestamps_string.split('\n').filter(line => line.trim() !== '');
    } else if (typeof result.output === 'string') {
      timestamps = result.output.split('\n').filter(line => line.trim() !== '');
    }

    return timestamps.map((line, index) => {
      // Try to split by first " - " or just space if not found, to separate time from text
      // The screenshot shows "0:05 - Text", so we can try to separate the time for styling.
      const match = line.match(/^(\d+:\d+(?::\d+)?)\s*[-–]\s*(.*)$/);
      if (match) {
        const seconds = timeToSeconds(match[1]);
        const link = generatedVideoId ? `https://youtu.be/${generatedVideoId}?t=${seconds}` : '#';
        return (
          <div key={index} className="timestamp-item">
            <a href={link} target="_blank" rel="noopener noreferrer" className="timestamp-time">{match[1]}</a>
            <span className="timestamp-text">{match[2]}</span>
          </div>
        );
      }
      return (
        <div key={index} className="timestamp-item">
          <span className="timestamp-text">{line}</span>
        </div>
      );
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUrl('');
  };

  return (
    <div className="hero-section">
      <div className="hero-content">
        <h1 className="hero-title">AI YouTube Timestamps & more</h1>
        <p className="hero-description">
          Generates timestamps for a given YouTube video using the bump-1.0 model—for uninterrupted, priority service at bumpups.com.
        </p>

        <div className="hero-form">
          <div className="input-wrapper">
            <input
              type="text"
              placeholder="Enter YouTube video URL"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setVideoDetails(null);
              }}
              className="url-input"
            />
            {url && (
              <button className="clear-input-btn" onClick={() => { setUrl(''); setError(null); setVideoDetails(null); }} aria-label="Clear input">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="language-select"
          >
            {languages.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>

          {historyMatch ? (
            <button onClick={handleViewSaved} className="btn-accent view-saved-btn">
              View already generated timestamps
            </button>
          ) : (
            <button onClick={handleGenerate} disabled={loading} className="btn-accent generate-btn">
              {loading ? 'Generating...' : 'Generate Timestamps'}
            </button>
          )}
        </div>

        {historyMatch && (
          <div className="history-match-message">
            You have already timestamped this video
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="hero-visual">
        {videoId ? (
          <div className="video-preview-container">
            <div className="video-preview-card">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${videoId}`}
                title={videoDetails?.title || "YouTube video player"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%' }}
              ></iframe>
            </div>
            {videoDetails?.title && <h3 className="video-title-preview">{videoDetails.title}</h3>}
          </div>
        ) : (
          <div className="hero-placeholder-visual">
            <p className="placeholder-text">Here will be the thumbnail of video</p>
          </div>
        )}
      </div>

      {showModal && result && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Timestamps</h2>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="timestamp-list">
                {renderTimestamps()}
              </div>

              <div className="copy-btn-wrapper">
                <button className="copy-btn" onClick={copyToClipboard}>
                  📋 Copy
                </button>
              </div>
            </div>

            <div className="modal-footer">
              Get uninterrupted, priority service — AI Creator Studio, local video uploads, and more at <a href="https://bumpups.com" target="_blank" rel="noopener noreferrer">bumpups.com</a>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timestamp;


